import React, { useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { useStoreState } from 'easy-peasy';
import qs from 'querystringify';

import Layout from '../../../components/global/Layout';
import { usePlaceInvoices } from './hooks/usePlaceInvoices';
import { toInvoiceKey } from './hooks/usePlaceInvoices';
import PlaceGate from './components/PlaceGate';
import PaymentsView from './components/PaymentsView';

import styles from './PlaceInvoices.module.css';

const PlaceInvoices = ({ history, location }) => {
  const { userId } = useStoreState(state => state.auth);

  const routeState = qs.parse(location.search);

  const {
    // first screen
    allPlacesStatus,
    allPlacesError,
    places,
    refreshAllPlaces,

    // transition
    hasPlace,
    isReadyForDetails,
    placeInvoicesError,
    refreshPlaceInvoices,

    // second screen
    selectedPlace,
    selectPlace,
    selectDropdownPlace,
    changePlace,
    filteredInvoices,
    invoiceSearch,
    setInvoiceSearch,
    selectedInvoice,
    selectInvoice,
    filteredOrders,
    orderSearch,
    setOrderSearch,
    selectedOrder,
    selectOrder,
    closeOrder,
  } = usePlaceInvoices(userId, { initialPlaceId: routeState.placeId ?? null });

  const handleSelectPlace = useCallback(
    placeId => {
      selectPlace(placeId);
      history.push(
        `${location.pathname}${qs.stringify({ placeId }, true)}`,
      );
    },
    [selectPlace, history, location.pathname],
  );

  const handleChangePlace = useCallback(() => {
    changePlace();
    history.push(location.pathname);
  }, [changePlace, history, location.pathname]);

  // Gate is ready once the all-places fetch settles (success or error).
  const gateReady =
    allPlacesStatus === 'success' ||
    allPlacesStatus === 'refreshing' ||
    allPlacesStatus === 'error';

  const showPaymentsView = hasPlace && isReadyForDetails;

  return (
    <Layout>
      <div className={styles.page}>
        <div className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <span className={styles.pageTitle}>Place Invoices</span>
            {hasPlace && selectedPlace && (
              <button className={styles.placeChip} onClick={handleChangePlace}>
                <span>🏪</span>
                <span className={styles.chipName}>{selectedPlace.name}</span>
                <span className={styles.chipChange}>Change ›</span>
              </button>
            )}
          </div>
          <div className={styles.topbarRight}>
          </div>
        </div>

        <div className={styles.content}>
          {/* ── Error banners ─────────────────────────────────────────────── */}
          {allPlacesError && !hasPlace && (
            <div className={styles.errorBanner}>
              {allPlacesError.message ?? 'Failed to load places.'}
              <button className={styles.retryBtn} onClick={refreshAllPlaces}>Retry</button>
            </div>
          )}
          {placeInvoicesError && hasPlace && (
            <div className={styles.errorBanner}>
              {placeInvoicesError.message ?? 'Failed to load invoice details.'}
              <button className={styles.retryBtn} onClick={refreshPlaceInvoices}>Retry</button>
            </div>
          )}

          {/* ── Loading: all places (initial, only when no placeId in URL) ── */}
          {!gateReady && !hasPlace && (
            <div className={styles.loadingScreen}>
              <div className={styles.loadingSpinner} />
              <div className={styles.loadingLabel}>Loading places…</div>
            </div>
          )}

          {/* ── PlaceGate ─────────────────────────────────────────────────── */}
          {gateReady && !hasPlace && (
            <PlaceGate
              places={places}
              onSelectPlace={handleSelectPlace}
              onSelectSuggestion={selectDropdownPlace}
              isLoading={false}
            />
          )}

          {/* ── Loading: place invoices ────────────────────────────────────── */}
          {hasPlace && !isReadyForDetails && !placeInvoicesError && (
            <div className={styles.loadingScreen}>
              <div className={styles.loadingSpinner} />
              <div className={styles.loadingLabel}>Loading invoice details…</div>
            </div>
          )}

          {/* ── PaymentsView ──────────────────────────────────────────────── */}
          {showPaymentsView && (
            <PaymentsView
              place={selectedPlace}
              invoices={filteredInvoices}
              selectedInvoice={selectedInvoice}
              orders={filteredOrders}
              selectedOrder={selectedOrder}
              invoiceSearch={invoiceSearch}
              orderSearch={orderSearch}
              onSearchInvoice={setInvoiceSearch}
              onSearchOrder={setOrderSearch}
              onSelectInvoice={inv => selectInvoice(toInvoiceKey(inv))}
              onSelectOrder={o => selectOrder(o.menuOrderId)}
              onCloseOrder={closeOrder}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default withRouter(PlaceInvoices);
